import { Box } from '@mui/material';
import SettingControl from 'components/SettingControl';
import { Switch } from 'components/Switch';
import {
  hdStreamToggled,
  mirrorVideoToggled,
  preserveAspectRatioToggled,
  selectHDVideoSettings,
  selectMirrorVideo,
  selectPreserveAspectRatio,
} from 'features/streaming/streamingSlice';
import { useVideoInputField } from 'hooks/useVideoInputField';
import VideoRenderer from 'components/VideoRenderer';
import { MediaDeviceChangeHandler } from 'features/join/publisher/JoinPreview';
import { type Control, type FieldValues } from 'react-hook-form';
import { TabPanelInner } from 'components/Tabs';
import DeviceSelect from 'components/DeviceSelect';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';

type VideoDeviceFieldValues = FieldValues & {
  videoinput: string;
};

export type VideoTabProps<TFieldValues extends VideoDeviceFieldValues = VideoDeviceFieldValues> = {
  videoStream: MediaStream | null;
  onVideoInputChange: MediaDeviceChangeHandler;
  control: Control<TFieldValues>;
  cameraDisabled?: boolean;
  cameraToggle?: React.ReactNode;
};

const VideoTab = <TFieldValues extends VideoDeviceFieldValues = VideoDeviceFieldValues>({
  videoStream,
  onVideoInputChange,
  cameraDisabled,
  control,
  cameraToggle,
}: VideoTabProps<TFieldValues>) => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation('settings');
  const { videoInputOptions, videoInputPlaceholder } = useVideoInputField();

  const preserveAspectRatio = useAppSelector(selectPreserveAspectRatio);
  const mirrorVideo = useAppSelector(selectMirrorVideo);

  const hdVideoSettings = useAppSelector(selectHDVideoSettings);

  const handlePreserveAspectRatioChange = () => {
    dispatch(preserveAspectRatioToggled());
  };

  const handleMirrorVideoChange = () => {
    dispatch(mirrorVideoToggled());
  };

  const handleHDStreamChange = () => {
    dispatch(hdStreamToggled());
  };

  return (
    <TabPanelInner>
      <VideoRenderer mediaStream={videoStream} sx={{ mb: 8 }} />
      {cameraToggle && cameraToggle}
      <DeviceSelect
        id="settings-video-select"
        name="videoinput"
        control={control as Control<VideoDeviceFieldValues>}
        icon="camera"
        onChange={onVideoInputChange}
        options={videoInputOptions}
        disabled={cameraDisabled}
        placeholder={videoInputPlaceholder}
        ariaLabel={t('device_dropdowns.videoinput.aria_label')}
      />

      <Box sx={{ mt: 8 }}>
        <SettingControl
          label={t('video.preserve_aspect_ratio')}
          alignItems="flex-start"
          control={
            <Switch
              onChange={handlePreserveAspectRatioChange}
              checked={preserveAspectRatio}
              slotProps={{
                input: {
                  'aria-label': preserveAspectRatio
                    ? t('video.preserve_aspec_ratio_disable_aria_label')
                    : t('video.preserve_aspec_ratio_enable_aria_label'),
                },
              }}
            />
          }
        />
      </Box>
      <Box sx={{ mt: 8 }}>
        <SettingControl
          label={t('video.mirror_video')}
          helperText={t('video.mirror_video_help')}
          alignItems="flex-start"
          control={
            <Switch
              onChange={handleMirrorVideoChange}
              checked={mirrorVideo}
              slotProps={{
                input: {
                  'aria-label': mirrorVideo
                    ? t('video.mirror_video_disable_aria_label')
                    : t('video.mirror_video_enable_aria_label'),
                },
              }}
            />
          }
        />
      </Box>
      <Box sx={{ mt: 8 }}>
        <SettingControl
          label={t('video.hd_stream_label')}
          helperText={t('video.hd_stream_warning')}
          alignItems="flex-start"
          control={
            <Switch
              onChange={handleHDStreamChange}
              checked={hdVideoSettings.enabled}
              slotProps={{
                input: {
                  'aria-label': hdVideoSettings.enabled
                    ? t('video.hd_stream_disable_aria_label')
                    : t('video.hd_stream_enable_aria_label'),
                },
              }}
            />
          }
        />
      </Box>
    </TabPanelInner>
  );
};

export default VideoTab;
